import { useEffect, useMemo } from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { ErrorBoundary } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import WhatsNext, { WHATS_NEXT_STATUS } from '#client/components/WhatsNext';
import { dashboard, invoicePayment } from '#client/constants/routes';
import useUnpaidInvoices from '#client/hooks/useUnpaidInvoices';
import useBnplRecoveryContext from '#client/pages/InvoicePayments/hooks/useBnplRecoveryContext';

import InvoiceCard from './components/InvoiceCard';
import PaymentAlert from './components/PaymentAlert';
import PaymentReturnHeader from './components/PaymentReturnHeader';
import {
  PAYMENT_STATUS,
  SUCCESS_ILLUSTRATION_SRC,
  ERROR_ILLUSTRATION_SRC,
  APP_SECTION,
  APP_PAGE_NAMES,
  CLICKSTREAM_EVENTS
} from './constants';
import { PaymentReturnPageRoot, PaymentReturnContent } from './PaymentReturn.style';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function centsToDollars(amountInCents) {
  const dollars = Number(amountInCents) / 100;
  return Number.isNaN(dollars) ? 0 : Number(dollars.toFixed(2));
}

function formatAmount(amountInCents) {
  return currencyFormatter.format(centsToDollars(amountInCents));
}

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get('status') || PAYMENT_STATUS.FAILURE;
  const companyName = searchParams.get('companyName') || '';
  const accountUuid = searchParams.get('accountUuid');
  const amountInCents = searchParams.get('amountInCents') || '0';
  const urlUnpaidCount = Number(searchParams.get('unpaidCount') || 0);

  const isSuccess = status === PAYMENT_STATUS.SUCCESS;
  const displayAmount = formatAmount(amountInCents);
  const illustrationSrc = isSuccess ? SUCCESS_ILLUSTRATION_SRC : ERROR_ILLUSTRATION_SRC;

  // accountUuid may be absent on this unauthenticated return page; useUnpaidInvoices skips when null.
  const {
    unpaidInvoices,
    loading: unpaidLoading,
    error: unpaidError
  } = useUnpaidInvoices(accountUuid);

  // Prefer the live unpaid-invoice count once loaded so the CTA branch matches what analytics reports
  // in dynamicProperties. Fall back to the URL count when the query is skipped, still in flight, or errored.
  const unpaidCount = accountUuid && !unpaidLoading && !unpaidError
    ? unpaidInvoices.length
    : urlUnpaidCount;
  const analyticsReady = !accountUuid || (!unpaidLoading && !unpaidError);

  const invoiceId = searchParams.get('invoiceId');
  const currentPaid = useMemo(() => {
    if (!isSuccess || !invoiceId) return undefined;
    return { id: invoiceId, amount: centsToDollars(amountInCents) };
  }, [isSuccess, invoiceId, amountInCents]);
  const errorMessage = !isSuccess ? searchParams.get('errorMessage') || undefined : undefined;

  // Line items are written to sessionStorage by InvoicePayments before the Klarna redirect
  // to avoid embedding large JSON in the return_url (browsers and Stripe cap URLs around 2–4KB).
  const items = useMemo(() => {
    if (!invoiceId) return [];
    try {
      const stored = sessionStorage.getItem(`invoice_lineitems_${invoiceId}`);
      if (!stored) return [];
      return JSON.parse(stored).map((item) => ({
        description: item.description || '',
        amount: currencyFormatter.format(Number(item.amount) || 0)
      }));
    } catch {
      return [];
    }
  }, [invoiceId]);

  // Clean up line-items entry once read — prevents stale data from accumulating
  // across multiple payment attempts in the same tab.
  useEffect(() => {
    if (invoiceId) sessionStorage.removeItem(`invoice_lineitems_${invoiceId}`);
  }, [invoiceId]);

  const { dynamicProperties } = useBnplRecoveryContext({
    unpaidInvoices,
    hadSuccessPayment: isSuccess,
    currentPaid,
    errorMessage
  });

  const appPageName = isSuccess
    ? APP_PAGE_NAMES.successfulPayment
    : APP_PAGE_NAMES.unsuccessfulPayment;

  let ctaEvent;
  if (!isSuccess) {
    ctaEvent = CLICKSTREAM_EVENTS.payRemainingInvoiceOnFailure;
  } else if (unpaidCount > 0) {
    ctaEvent = CLICKSTREAM_EVENTS.payRemainingInvoiceOnSuccess;
  } else {
    ctaEvent = CLICKSTREAM_EVENTS.dashboardCta;
  }

  const ctaTarget = isSuccess && unpaidCount === 0 ? dashboard : invoicePayment;

  const ctaClickstreamProps = useMemo(
    () => ({ ...ctaEvent, dynamicProperties }),
    [ctaEvent, dynamicProperties]
  );

  const handleCtaClick = () => {
    if (ctaTarget === dashboard) {
      navigate(dashboard);
      return;
    }
    const params = new URLSearchParams();
    if (accountUuid) params.set('accountUuid', accountUuid);
    if (companyName) params.set('companyName', companyName);
    navigate(`${invoicePayment}?${params.toString()}`);
  };

  return (
    <ErrorBoundary id="src/pages/PaymentReturn#PaymentReturn">
      {analyticsReady && (
        <PageViewedTracker
          appPageName={appPageName}
          appSection={APP_SECTION}
          dynamicProperties={dynamicProperties}
        />
      )}
      <PaymentReturnPageRoot>
        <PaymentReturnHeader />
        <PaymentReturnContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            <Stack flex={1} gap={3}>
              {companyName && (
                <Text size="sm" color="secondary" uppercase>
                  {companyName}
                </Text>
              )}
              <Text size="2xl" weight="bold" family="secondary">
                <FormattedMessage
                  id={isSuccess
                    ? 'dashboard.paymentReturn.title.success'
                    : 'dashboard.paymentReturn.title.failure'}
                />
              </Text>
              <Text size="md" color="secondary">
                <FormattedMessage
                  id={isSuccess
                    ? 'dashboard.paymentReturn.subtitle.success'
                    : 'dashboard.paymentReturn.subtitle.failure'}
                />
              </Text>

              {!isSuccess && <PaymentAlert />}

              <InvoiceCard
                isSuccess={isSuccess}
                amount={displayAmount}
                invoiceNumber={invoiceId || undefined}
                items={items}
              />

              <WhatsNext
                status={isSuccess ? WHATS_NEXT_STATUS.SUCCESS : WHATS_NEXT_STATUS.FAILURE}
                unpaidCount={unpaidCount}
              />

              <Button
                clickstreamProps={ctaClickstreamProps}
                variant="contained"
                size="large"
                onClick={handleCtaClick}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                <FormattedMessage
                  id={isSuccess && unpaidCount === 0
                    ? 'dashboard.paymentReturn.cta.success'
                    : 'dashboard.paymentReturn.cta.failure'}
                />
              </Button>
            </Stack>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1
              }}
            >
              <SharedAsset
                alt=""
                src={illustrationSrc}
                width={200}
                height={200}
              />
            </Box>
          </Box>
        </PaymentReturnContent>
      </PaymentReturnPageRoot>
    </ErrorBoundary>
  );
}
